import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { captureApplicationIds, useWebClientApi } from "src/api";
import { isAbortError } from "src/api/utils/isAbortError";
import { useAuth, useUser } from "src/context";
import type { DtoApplicationProgressResp } from "@porchpass/api-client";
import { getIdsFromPath } from "src/utils";

const DOCUSIGN_PROGRESS_DELAY_MS = 2000;

export const ProgressInitializer = ({
  children,
}: {
  children: (props: {
    formData: Partial<DtoApplicationProgressResp>;
    setFormData: React.Dispatch<
      React.SetStateAction<Partial<DtoApplicationProgressResp>>
    >;
    isLoading: boolean;
    refreshProgress: () => Promise<void>;
  }) => React.ReactNode;
}) => {
  const { user: firebaseUser } = useAuth();
  const { userData, setUserData } = useUser();
  const api = useWebClientApi();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<Partial<DtoApplicationProgressResp>>(
    {},
  );
  const [isLoading, setIsLoading] = useState(true);
  const cancelledRef = useRef(false);
  const apiRef = useRef(api);
  const setUserDataRef = useRef(setUserData);
  const userDataRef = useRef(userData);

  apiRef.current = api;
  setUserDataRef.current = setUserData;
  userDataRef.current = userData;

  const hasDocusignBorrowerId = searchParams.get("signer_id") ?? "";
  // DocuSign appends ?event=<sessionEndType> to the return URL. Only a
  // completed signing records the e-signature — cancel, decline, "Finish
  // Later", and timeouts must not, or the borrower is stuck awaiting a
  // webhook that never arrives (DEA-946).
  const hasCompletedDocusignSigning =
    searchParams.get("event") === "signing_complete";

  const fetchProgress = useCallback(
    async (signal?: AbortSignal) => {
      const { application_id, dealer_id } = getIdsFromPath();
      const dealerId = userDataRef.current?.dealer_id ?? dealer_id;
      const applicationId =
        userDataRef.current?.application_id ?? application_id;

      if (
        !firebaseUser ||
        !dealerId ||
        !applicationId ||
        cancelledRef.current ||
        signal?.aborted
      ) {
        return null;
      }

      try {
        const data = await apiRef.current.calculateProgress(applicationId, {
          signal,
        });

        if (cancelledRef.current || signal?.aborted) {
          return null;
        }

        if (data && setUserDataRef.current) {
          captureApplicationIds(data, setUserDataRef.current);
          setFormData(data);
        }

        return data;
      } catch (error) {
        if (isAbortError(error)) {
          return null;
        }
        throw error;
      }
    },
    [firebaseUser],
  );

  const refreshProgress = useCallback(async () => {
    await fetchProgress();
  }, [fetchProgress]);

  useEffect(() => {
    if (!firebaseUser) return;

    const abortController = new AbortController();
    const { signal } = abortController;
    cancelledRef.current = false;

    const initializeProgress = async () => {
      setIsLoading(true);

      try {
        const { dealer_id } = getIdsFromPath();
        const dealerId = userDataRef.current?.dealer_id ?? dealer_id;

        if (hasCompletedDocusignSigning && hasDocusignBorrowerId && dealerId) {
          try {
            await apiRef.current.markSigned(hasDocusignBorrowerId, { signal });
          } catch (error) {
            if (isAbortError(error)) {
              return;
            }
            throw error;
          }

          await new Promise((resolve) =>
            setTimeout(resolve, DOCUSIGN_PROGRESS_DELAY_MS),
          );

          if (signal.aborted || cancelledRef.current) {
            return;
          }
        }

        await fetchProgress(signal);
      } catch (error) {
        if (!isAbortError(error)) {
          throw error;
        }
      } finally {
        if (!signal.aborted && !cancelledRef.current) {
          setIsLoading(false);
        }
      }
    };

    void initializeProgress();

    return () => {
      cancelledRef.current = true;
      abortController.abort();
    };
  }, [
    firebaseUser,
    fetchProgress,
    hasDocusignBorrowerId,
    hasCompletedDocusignSigning,
  ]);

  return (
    <>
      {children({
        formData,
        setFormData,
        isLoading,
        refreshProgress,
      })}
    </>
  );
};
